import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import {
  AppDispatch,
  fullstorySelectors,
  getCheckedForInsights,
  getInsightsByDxIdHash,
  InsightDetail,
  InstantInsightActions,
  MetricsInsightsRequestActions,
} from '@fullstory/redux';
import { DxIdVersion } from '@fullstory/proto/fs/api/proactiveinsights';
import { hashObject } from '@fullstory/util/hash';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { noop } from 'lodash';

export const useInsightsForMetric = (
  dxIdVersion: DxIdVersion,
  enabled: boolean,
  pollingTimes: number[] = [2000, 4000, 8000], // time in ms
) => {
  const flags = useFlags();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const dispatch = useDispatch<AppDispatch>();
  const checkedForInsights = useSelector(getCheckedForInsights);
  const metricInsightsEnabled = flags.hasBit(Flag.FeatMetricInsights);

  const insightsById = useSelector(getInsightsByDxIdHash);
  const dxIdVersionHash = hashObject(dxIdVersion);
  const metricInsights: InsightDetail[] = insightsById[dxIdVersionHash] ?? [];

  useEffect(() => {
    let unsubscribe = noop;

    if (metricInsightsEnabled && dxIdVersion.id !== '' && enabled) {
      unsubscribe = pollGetInsights(
        dispatch,
        orgId,
        dxIdVersion,
        checkedForInsights,
        metricInsights,
        pollingTimes,
      );
    }

    return () => {
      unsubscribe();
    };
    // if checkedForInsights changes, we don't want to dispatch the API call again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orgId, dxIdVersion.id, dxIdVersion.version, metricInsightsEnabled, enabled]);

  return metricInsights;
};

/**
 * Insights are considered equal if they have the same set of insightId's.
 */
const insightsEqual = (a: InsightDetail[], b: InsightDetail[]) => {
  if (a.length !== b.length) return false;

  const aIds = new Set(a.map(insight => insight.insightId));
  const bIds = new Set(b.map(insight => insight.insightId));

  return [...aIds].every(id => bIds.has(id));
};

// check for new insights
const pollGetInsights = (
  dispatch: AppDispatch,
  orgId: string,
  dxIdVersion: DxIdVersion,
  checkedForInsights: Set<number>,
  currentInsights: InsightDetail[],
  times: number[],
) => {
  const dxIdHash = hashObject(dxIdVersion);
  if (!checkedForInsights.has(dxIdHash)) {
    // TODO - this should have some expiration, probably inline with other metric computation caching
    dispatch(InstantInsightActions.addCheckedForInsightsHash(dxIdHash));

    let receivedNewInsights = false;

    const fetchInsights = () => {
      dispatch(MetricsInsightsRequestActions.getInsights(orgId, true, dxIdVersion)).then(
        insights => {
          receivedNewInsights = !insightsEqual(currentInsights, insights.insights);
        },
      );
    };

    // dispatch right away for any existing insights
    fetchInsights();

    let pollingRequestsSent = 0;
    // dispatch for other insights that might be created
    const timeouts = times.map(time =>
      setTimeout(() => {
        // if new insights have already been received, no op this
        if (receivedNewInsights) return;

        pollingRequestsSent += 1;
        fetchInsights();
      }, time),
    );

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));

      // if cancelling before exhausting polling and getting back new insights,
      // remove the "check" from the cache so it can be retried later
      const pollingExhausted = times.length > 0 && pollingRequestsSent === times.length;
      if (!receivedNewInsights && !pollingExhausted) {
        dispatch(InstantInsightActions.removeCheckedForInsightsHash(dxIdHash));
      }
    };
  }

  return noop;
};
